"use client";
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/ui/pagination";
import Link from "next/link";

type Scheme = {
	id: number;
	name: string;
	description?: string | null;
	department?: string | null;
	category?: string | null;
	application_link?: string | null;
	is_active?: boolean | null;
};

export default function Home() {
	const [q, setQ] = React.useState("");
	const [category, setCategory] = React.useState("");
	const [state, setState] = React.useState("");
	const [schemes, setSchemes] = React.useState<Scheme[]>([]);
	const [total, setTotal] = React.useState(0);
	const [limit] = React.useState(12);
	const [offset, setOffset] = React.useState(0);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	const fetchSchemes = React.useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const params = new URLSearchParams();
			if (q) params.set("q", q);
			if (category) params.set("category", category);
			if (state) params.set("state", state);
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			const res = await fetch(`/api/schemes?${params.toString()}`);
			const data = await res.json();
			if (!res.ok) throw new Error(data?.error || "Failed to load");
			setSchemes(data.schemes || []);
			setTotal(data.total || 0);
		} catch (e) {
			const msg = e instanceof Error ? e.message : "Failed to load";
			setError(msg);
		} finally {
			setLoading(false);
		}
	}, [q, category, state, limit, offset]);

	React.useEffect(() => {
		fetchSchemes();
	}, [fetchSchemes]);

	const onSearch = () => {
		setOffset(0);
		fetchSchemes();
	};

	return (
		<div className="space-y-8">
			<section className="flex flex-col gap-3">
				<h1 className="text-2xl font-semibold tracking-tight">
					Discover Government Schemes
				</h1>
				<p className="text-[var(--color-muted)] max-w-2xl">
					Search and filter to find schemes that match your profile.
					Click a card to see full details, eligibility, and how to
					apply.
				</p>
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
					<Input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder="Search by name, department, description"
					/>
					<Select
						value={category}
						onChange={(e) => setCategory(e.target.value)}
					>
						<option value="">All categories</option>
						<option>education</option>
						<option>health</option>
						<option>agriculture</option>
						<option>welfare</option>
					</Select>
					<Select
						value={state}
						onChange={(e) => setState(e.target.value)}
					>
						<option value="">All states</option>
						<option>Gujarat</option>
						<option>Maharashtra</option>
						<option>Karnataka</option>
					</Select>
					<Button variant="primary" onClick={onSearch}>
						Search
					</Button>
				</div>
			</section>

			<section>
				{error ? (
					<div className="card p-4 text-[var(--color-danger)]">
						{error}
					</div>
				) : null}
				{loading ? (
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
						{Array.from({ length: 8 }).map((_, i) => (
							<div key={i} className="card p-5 animate-pulse">
								<div className="h-5 w-2/3 bg-[var(--accent)] rounded" />
								<div className="mt-3 space-y-2">
									<div className="h-3 w-full bg-[var(--accent)] rounded" />
									<div className="h-3 w-5/6 bg-[var(--accent)] rounded" />
								</div>
							</div>
						))}
					</div>
				) : (
					<>
						{schemes.length === 0 ? (
							<div className="card p-6 text-center text-[var(--color-muted)]">
								No schemes found. Try adjusting filters.
							</div>
						) : (
							<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
								{schemes.map((s) => (
									<Card
										key={s.id}
										className="overflow-hidden"
									>
										<CardContent>
											<div className="flex items-start justify-between">
												<h3 className="font-medium leading-tight line-clamp-2">
													{s.name}
												</h3>
												{s.is_active ? (
													<Badge>Active</Badge>
												) : (
													<Badge>Inactive</Badge>
												)}
											</div>
											{s.description ? (
												<p className="mt-2 text-sm text-[var(--color-muted)] line-clamp-3">
													{s.description}
												</p>
											) : null}
											<div className="mt-4 flex flex-wrap gap-2 text-xs text-[var(--color-muted)]">
												{s.category ? (
													<span className="badge">
														{s.category}
													</span>
												) : null}
												{s.department ? (
													<span className="badge">
														{s.department}
													</span>
												) : null}
											</div>
											<div className="mt-4 flex gap-2">
												<Link
													className="btn"
													href={`/schemes/${s.id}`}
												>
													Details
												</Link>
												{s.application_link ? (
													<a
														className="btn"
														href={
															s.application_link
														}
														target="_blank"
														rel="noreferrer"
													>
														Apply
													</a>
												) : null}
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						)}
						<Pagination
							total={total}
							limit={limit}
							offset={offset}
							onChange={setOffset}
						/>
					</>
				)}
			</section>
		</div>
	);
}
